import React from 'react';
import { Avatar } from 'components/common';
import CheckboxRenderer from "components/common/ag-grid/CheckboxRenderer";
import DateFormatRenderer from "../components/DateFormatRenderer";
import NumberRenderer from "components/common/ag-grid/NumberRenderer";
import { formatDate } from "helpers/utils";
import FormatPercentRenderer from "components/common/ag-grid/FormatPerentRenderer";
import WeightFormatRenderer from "./WeightFormatRenderer";
import StatusBuildingFormatRenderer from "./StatusFormatRenderer";
import ProjectNameRenderer from "./ProjectNameRenderer";
import APDFormatRenderer from "../components/APDFormatRenderer";
import ShopFormatRenderer from "../components/ShopFormatRenderer";
import ShipmentFormatRenderer from "./ShipmentFormatRenderer";
import JobFormatRenderer from "../components/JobFormatRenderer";
import { PersonRenderer, iconCell } from "components/common/ag-grid";
import { MdPublic } from 'react-icons/md';
import { LuMapPin } from 'react-icons/lu';
import { Link } from 'react-router-dom';

export const columnDefs = [
  {
    headerName: "PROJECT DETAILS",
    children: [
      { 
        headerName: 'ID#',
        field: 'id',
        hide: true,
      },
      { 
        headerName: 'No.',
        field: 'j_sort',
        minWidth: 90
      },
      { 
        headerName: 'Quote number.',
        field: 'project.q_number',
        minWidth: 120,
        cellRenderer: (params) => {
          const {project} = params.data || {};
          return (
            <Link to={`/crm/projects/${project?.id}`} target="_blank">{project?.q_number}</Link>
          )
        }
      },
      { 
        headerName: 'P/J number',
        field: 'j_number',
        width: 130, minWidth: 100, flex: 0,
        pinned: 'left',
        cellRenderer: JobFormatRenderer
      },
      {
        headerName: 'Customer name',
        field: 'project.customer_name',
        minWidth: 200,
        cellRenderer: (params) => {
          const { project } = params.data || {};
          if (!project?.customer_name) return null;
          // Was linking to /crm/projects/<customer_id> — wrong entity and id.
          return (
            <span className="tw-inline-flex tw-items-center tw-gap-2 tw-max-w-full">
              <Avatar name={project.customer_name} alt={project.customer_name} size="xs" className="tw-shrink-0" />
              <Link to={`/crm/customers/${project.customer_id}`} target="_blank" className="tw-truncate">{project.customer_name}</Link>
            </span>
          )
        }
      },
      {
        headerName: 'Project name',
        field: 'project.name',
        minWidth: 200,
        // The renderer stacks project name + building + notes + identical-count
        // on multiple lines; without autoHeight the fixed row height clips
        // everything below the first line.
        autoHeight: true,
        wrapText: true,
        cellRenderer: ProjectNameRenderer
      },
      {
        headerName: "SALE AREA",
        children: [
          {
            headerName: 'Sales incharge',
            field: 'project.sales_incharge',
            minWidth: 120,
            cellRenderer: PersonRenderer
          },
          {
            headerName: 'Region',
            field: 'sales_unit.region',
            minWidth: 130,
            cellRenderer: iconCell(MdPublic)
          },
          {
            headerName: 'Location',
            field: 'project.pj_location',
            minWidth: 220,
            cellRenderer: iconCell(LuMapPin)
          },
          {
            headerName: 'Province',
            field: 'project.pj_province',
            minWidth: 150,
            cellRenderer: iconCell(LuMapPin)
          },
          // { 
          //   headerName: 'Country',
          //   field: 'project.pj_country',
          //   minWidth: 110
          // },
        ]
      },
      {
        headerName: "TOTAL WEIGHT (M.T)",
        children: [
          { 
            headerName: 'JAF',
            field: 'jaf_weight',
            minWidth: 140,
            cellRenderer: WeightFormatRenderer
          },
          { 
            headerName: 'Final EST',
            field: 'est_final_weight',
            minWidth: 140,
            cellRenderer: WeightFormatRenderer
          },
          { 
            headerName: 'BOM',
            field: 'bom_weight',
            minWidth: 140,
            cellRenderer: WeightFormatRenderer
          },
        ]
      },
      {
        headerName: " ",
        children: [
          { 
            headerName: 'Area (m2)',
            field: 'area',
            minWidth: 120,
            cellRenderer: NumberRenderer
          },
          { 
            headerName: 'Full released BOM',
            field: 'full_released_bom',
            minWidth: 180,
            cellRenderer: CheckboxRenderer
          },
        ]
      }
    ]
  },
  {
    headerName: "CUSTOMER SERVICE DEPT.",
    children: [
      { 
        headerName: 'Job entry date',
        field: 'job_entry_date',
        minWidth: 150,
        cellRenderer: DateFormatRenderer
      },
      { 
        headerName: 'Lastest CO date',
        field: 'lastest_co_date',
        minWidth: 150,
        cellRenderer: DateFormatRenderer
      },
      { 
        headerName: 'Contract date',
        field: 'contract_date',
        minWidth: 150,
        cellRenderer: (params) => {
          const data = params.data
          const value = params.value
  
          return value === null || value === "" ? (<span className="tw-text-red-600">{data.contract_note}</span>) : formatDate(value, "dd/MM/yy")
        }
      },
    ]
  },
  {
    headerName: "FINANCIAL DEPARTMENT",
    children: [
      {
        headerName: "PAYMENT TERMS",
        children: [
          { 
            headerName: 'Payment description',
            field: 'payment_term.description',
            minWidth: 200,
            cellRenderer: (params) => {
              return <span className="tw-text-blue-600">{params.value}</span>
            }
          },
          { 
            headerName: 'DP',
            field: 'payment_term.down_payment',
            minWidth: 100,
            cellRenderer: DateFormatRenderer
          },
          { 
            headerName: 'Received up to',
            field: 'payment_term.received_up_to',
            minWidth: 120,
            cellRenderer: FormatPercentRenderer
          },
          { 
            headerName: 'BG/LC',
            field: 'payment_term.bank_guarantee_letter_of_credit',
            minWidth: 80,
            cellRenderer: CheckboxRenderer
          },
          { 
            headerName: 'APG',
            field: 'payment_term.advance_payment_guarantee',
            minWidth: 80,
            cellRenderer: CheckboxRenderer
          },
          { 
            headerName: 'PB',
            field: 'payment_term.performance_bond',
            minWidth: 80,
            cellRenderer: CheckboxRenderer
          },
          { 
            headerName: 'Penalty',
            field: 'payment_term.penalty',
            minWidth: 80,
            cellRenderer: CheckboxRenderer
          },
          { 
            headerName: 'CFS',
            field: 'payment_term.clear_for_shipment',
            minWidth: 80,
            cellRenderer: CheckboxRenderer
          },
        ]
      }
    ]
  },
  {
    headerName: "ENGINEERING DEPT.",
    children: [
      {
        headerName: "APD",
        children: [
          { 
            headerName: 'APD Schedule',
            field: 'apds.apd_schedule',
            minWidth: 120,
            cellRenderer: APDFormatRenderer
          },
          { 
            headerName: 'APD Release',
            field: 'apds.apd_release',
            minWidth: 120,
            cellRenderer: APDFormatRenderer
          },
        ]
      },
      { 
        headerName: 'RAD',
        field: 'return_approval_drawing',
        minWidth: 120,
        cellRenderer: DateFormatRenderer
      },
      {
        headerName: "SHOP DWGS",
        children: [
          { 
            headerName: 'Shop DWGS Schedule',
            field: 'shops.shop_schedule',
            minWidth: 120,
            cellRenderer: ShopFormatRenderer
          },
          { 
            headerName: 'Shop DWGS Release',
            field: 'shops.shop_release',
            minWidth: 120,
            cellRenderer: ShopFormatRenderer
          },
          { 
            headerName: 'Shop DWGS released (Y/N)',
            field: 'shop_dwgs_released_status',
            minWidth: 140,
            cellRenderer: CheckboxRenderer
          },
        ]
      },
      {
        headerName: "ERECTION DWGS",
        children: [
          { 
            headerName: 'Erec DWGS Schedule',
            field: 'shops.erc_schedule',
            minWidth: 120,
            cellRenderer: ShopFormatRenderer
          },
          { 
            headerName: 'Erec DWGS Release',
            field: 'shops.erc_release',
            minWidth: 120,
            cellRenderer: ShopFormatRenderer
          },
          { 
            headerName: 'Erec DWGS released (Y/N)',
            field: 'erc_drawings_released_status',
            minWidth: 140,
            cellRenderer: CheckboxRenderer
          },
        ]
      }
    ]
  },
  { 
    headerName: 'Production Released',
    field: 'production_released',
    minWidth: 140,
    cellRenderer: CheckboxRenderer
  },
  {
    headerName: "SHIPMENT DATE",
    children: [
      { 
        headerName: 'Required by customer',
        field: 'shipments.required_by_customer',
        minWidth: 140,
        cellRenderer: ShipmentFormatRenderer
      },
      { 
        headerName: 'CSD committed',
        field: 'shipments.csd_committed',
        minWidth: 140,
        cellRenderer: ShipmentFormatRenderer
      },
      { 
        headerName: 'CSD actual',
        field: 'shipments.csd_actual',
        minWidth: 140,
        cellRenderer: ShipmentFormatRenderer
      },
      { 
        headerName: 'Status',
        field: 'shipment_status',
        minWidth: 120,
        cellRenderer: FormatPercentRenderer
      },
    ]
  },
  {
    headerName: " ",
    children: [
      { 
        headerName: 'Erec by Pebsteel',
        field: 'erc_by_pebsteel',
        minWidth: 110,
        cellRenderer: CheckboxRenderer
      },
      { 
        headerName: 'PEB Scope',
        field: 'peb_scope',
        minWidth: 110
      },
    ]
  },
  {
    headerName: "SCOPE OF WORK",
    children: [
      { 
        headerName: 'Red Steel',
        field: 'red_steel',
        minWidth: 100,
        cellRenderer: CheckboxRenderer
      },
      { 
        headerName: 'Sheeted',
        field: 'sheeted',
        minWidth: 100,
        cellRenderer: CheckboxRenderer
      },
      { 
        headerName: 'Insulation',
        field: 'insulation',
        minWidth: 100,
        cellRenderer: CheckboxRenderer
      },
    ]
  },
  {
    headerName: " ",
    children: [
      { 
        headerName: 'Building status',
        field: 'status.name',
        minWidth: 160,
        cellRenderer: StatusBuildingFormatRenderer
      },
      { 
        headerName: 'Active',
        field: 'active',
        minWidth: 80,
        cellRenderer: CheckboxRenderer
      },
      { 
        headerName: 'CSE inchage',
        field: 'csd_incharges',
        minWidth: 140,
        cellRenderer: params => {
          return params.value?.join(", ")
        }
      },
      { 
        headerName: 'Deeply remark',
        field: 'deeply_remark',
        minWidth: 200,
        cellRenderer: (params) => {
          return <span className="tw-text-red-600">{params.value}</span>
        }
      },
      { 
        headerName: 'Current status',
        field: 'current_status',
        minWidth: 180,
        cellRenderer: (params) => {
          return <span className="tw-text-blue-600">{params.value}</span>
        }
      },
      { 
        headerName: 'Undelivery',
        field: 'undelivery',
        minWidth: 110,
        cellRenderer: NumberRenderer
      },
      { 
        headerName: 'Notes',
        field: 'sts_notes',
        minWidth: 130,
        cellRenderer: (params) => {
          return <span className="tw-text-red-600">{params.value}</span>
        }
      },
      {
        headerName: 'Project type',
        field: 'project.project_type',
        minWidth: 120,
        valueGetter: (params) => params.data?.project?.project_type,
        cellRenderer: (params) => {
          if (!params.value) return <span className="tw-text-slate-400">—</span>;
          const isHotRoll = params.value === 'hot_roll';
          return (
            <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${isHotRoll ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-primary-100 tw-text-primary-700'}`}>
              {isHotRoll ? 'Hot Roll' : 'Built Up'}
            </span>
          );
        },
      },
    ]
  }
]